import * as React from 'react';

import { useMutation, useQuery } from '@apollo/client';

import { useTrackingContext } from '@context/tracking/TrackingContext';

import { getProductsByIdBatched_vpProductByIds } from '../BatchVPProductQuery/queries/__generated__/getProductsByIdBatched';

import {
  handleAddToWishlistGTM,
  handleRemoveFromWishlistGTM,
  WishlistActionLocation,
} from './gtm/wishlistManipulation';
import { ICallbackParameters } from './model';
import {
  addToWishlist as addToWishlistType,
  addToWishlistVariables,
} from './queries/__generated__/addToWishlist';
import { isWishlistEnabled } from './queries/__generated__/isWishlistEnabled';
import {
  removeFromWishlist,
  removeFromWishlistVariables,
} from './queries/__generated__/removeFromWishlist';
import addToWishlistMutation from './queries/addToWishlist.graphql';
import isWishlistEnabledQuery from './queries/isWishlistEnabled.graphql';
import removeFromWishlistMutation from './queries/removeFromWishlist.graphql';
import { updateWishlistedCache } from './updateWishlistedCache';

export const publishUpdateWishlistCountEvent = () => {
  if (window.wishlistService) {
    window.wishlistService.count();
  } else if (document) {
    // publish a custom event only when the wihlistService is unavailable because
    // when it is available the event is published inside the wishlistService count() method
    const event = new CustomEvent('header:wishlist:overview:update');
    document.dispatchEvent(event);
  }
};

interface IUseWishlistActions {
  productId: string;
  wishlisted: boolean;
  updateCallback?: (
    options: ICallbackParameters
  ) => getProductsByIdBatched_vpProductByIds;
  onCompleted?: (productId: string, wishlisted: boolean) => void;
}

export const useWishlistActionsFactory = ({
  updateCallback,
  onCompleted,
}: Pick<IUseWishlistActions, 'updateCallback' | 'onCompleted'>) => {
  const [loading, setLoading] = React.useState(false);
  const [add] = useMutation<addToWishlistType, addToWishlistVariables>(
    addToWishlistMutation
  );

  const [remove] = useMutation<removeFromWishlist, removeFromWishlistVariables>(
    removeFromWishlistMutation
  );

  const wishlistSettings = useQuery<isWishlistEnabled>(isWishlistEnabledQuery);
  const { getTimeFromInit } = useTrackingContext();

  const handleWishlistClick = React.useCallback(
    async (
      productId: string,
      wishlisted: boolean,
      actionLocation: WishlistActionLocation,
      trackingPromoLabels: string[] = []
    ) => {
      setLoading(true);

      if (wishlisted) {
        try {
          await remove({
            variables: { productId },
            update: (
              cache,
              {
                data: {
                  WishlistRemoveProduct: { products },
                },
              }
            ) => {
              const removedProduct = updateCallback
                ? updateCallback({ products, cache })
                : (updateWishlistedCache({ cache, products, productId }) || {})
                    .vpProductById;

              if (removedProduct) {
                handleRemoveFromWishlistGTM(
                  removedProduct,
                  getTimeFromInit(),
                  actionLocation
                );
              }
            },
          });

          publishUpdateWishlistCountEvent();
          onCompleted?.(productId, false);
        } finally {
          setLoading(false);
        }
      } else {
        try {
          await add({
            variables: { productId },
            update: (
              cache,
              {
                data: {
                  WishlistAddProduct: { products },
                },
              }
            ) => {
              const addedProduct = updateCallback
                ? updateCallback({ products, cache })
                : (updateWishlistedCache({ cache, products, productId }) || {})
                    .vpProductById;

              if (addedProduct) {
                handleAddToWishlistGTM(
                  addedProduct,
                  getTimeFromInit(),
                  actionLocation,
                  trackingPromoLabels
                );
              }
            },
          });

          publishUpdateWishlistCountEvent();
          onCompleted?.(productId, true);
        } finally {
          setLoading(false);
        }
      }
    },
    [add, onCompleted, remove, updateCallback, getTimeFromInit]
  );

  const isWishlistAllowed =
    !wishlistSettings.loading &&
    !wishlistSettings.error &&
    wishlistSettings.data?.Settings?.isWishlistAllowed;

  return React.useMemo(
    () => ({
      handleWishlistClick,
      isWishlistAllowed,
      loading,
    }),
    [handleWishlistClick, isWishlistAllowed, loading]
  );
};

export const useWishlistActions = ({
  productId,
  wishlisted,
  updateCallback,
  onCompleted,
}: IUseWishlistActions) => {
  const { handleWishlistClick, isWishlistAllowed, loading } =
    useWishlistActionsFactory({ updateCallback, onCompleted });

  return React.useMemo(
    () => ({
      handleWishlistClick: (
        actionLocation: WishlistActionLocation,
        trackingPromoLabels: string[] = []
      ) =>
        handleWishlistClick(
          productId,
          wishlisted,
          actionLocation,
          trackingPromoLabels
        ),
      isWishlistAllowed,
      loading,
    }),
    [handleWishlistClick, isWishlistAllowed, loading, productId, wishlisted]
  );
};
